import { useParams, useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useAccountStatement } from './useAccountStatement';
import { StatementHeader } from './components/StatementHeader';
import { StatementFilters } from './components/StatementFilters';
import { StatementSummary } from './components/StatementSummary';
import { StatementTable } from './components/StatementTable';

const formatPeriodLabel = (from: string, to: string): string => {
  const f = new Date(from + 'T00:00:00');
  const t = new Date(to + 'T00:00:00');
  const fLabel = f.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  const tLabel = t.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  return `${fLabel} – ${tLabel}`;
};

export default function AccountStatementPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    account, accountLoading,
    transactions, loading,
    summary, filters,
    updateFilters, setQuickPeriod,
    refetch,
  } = useAccountStatement(id);

  const currentPeriodBalance = useMemo(() => {
    const withBalance = transactions.find(t => t.runningBalance !== null);
    return withBalance?.runningBalance ?? null;
  }, [transactions]);

  const periodLabel = formatPeriodLabel(filters.dateFrom, filters.dateTo);

  if (!accountLoading && !account) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
          <i className="ri-bank-line text-3xl text-gray-300" />
        </div>
        <p className="text-base font-700 text-gray-600 mb-1">Conta não encontrada</p>
        <p className="text-sm text-gray-400 mb-5">Esta conta não existe ou não pertence ao seu usuário.</p>
        <button
          onClick={() => navigate('/contas')}
          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line" />
          Voltar para contas
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header + account info */}
      <StatementHeader
        account={account}
        loading={accountLoading}
        currentBalance={currentPeriodBalance}
        periodLabel={periodLabel}
      />

      {/* Summary cards */}
      <StatementSummary
        summary={summary}
        currency={account?.currency ?? 'BRL'}
        loading={loading}
      />

      {/* Filters */}
      <StatementFilters
        filters={filters}
        onChange={updateFilters}
        onQuickPeriod={setQuickPeriod}
        totalCount={transactions.length}
      />

      {/* Table section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-700 text-gray-700">Movimentações</h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-500">
              {transactions.length} registro{transactions.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-gray-400 bg-amber-50 border border-amber-100 px-2.5 py-1.5 rounded-lg">
              <i className="ri-information-line text-amber-500" />
              <span>Saldo calculado sobre lançamentos <strong>pagos/recebidos</strong></span>
            </div>
            <button
              onClick={refetch}
              disabled={loading}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-emerald-600 hover:border-emerald-200 hover:bg-emerald-50 disabled:opacity-50 cursor-pointer transition-colors"
              title="Atualizar"
            >
              <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <StatementTable
          transactions={transactions}
          currency={account?.currency ?? 'BRL'}
          loading={loading}
        />
      </div>

      {/* Legend */}
      {!loading && transactions.length > 0 && (
        <div className="flex items-center gap-4 flex-wrap pb-2">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <span className="text-xs text-gray-400">Receita recebida</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="text-xs text-gray-400">Despesa paga</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-teal-400" />
            <span className="text-xs text-gray-400">Transferência</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
            <span className="text-xs text-gray-400">Pendente (não afeta saldo)</span>
          </div>
        </div>
      )}
    </div>
  );
}
